import { useState, useMemo } from "react";
import { Plus, Minus, X, ChevronDown, ChevronUp, Check } from "lucide-react";
import { useGetSkills, useGetSkillMd } from "../../../api/queryHooks";
import InlineSpinner from "../../shared/Spinner/InlineSpinner";

/**
 * AgentSkillsSection — Inline expandable skill picker for attaching org skills to an agent.
 */
const AgentSkillsSection = ({ toolsJsonContent, onChange, orgId }) => {
  const [panelOpen, setPanelOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [expandedSkillMd, setExpandedSkillMd] = useState(null); // skill_id of open SKILL.md row
  const { data: skillsData, isLoading } = useGetSkills(orgId);

  const installedSkills = useMemo(
    () => (skillsData?.skills || []).filter((s) => s.enabled),
    [skillsData]
  );

  const currentSkills = useMemo(() => {
    try { return JSON.parse(toolsJsonContent)?.skills || []; }
    catch { return []; }
  }, [toolsJsonContent]);

  // Group by source_name
  const skillGroups = useMemo(() => {
    const map = {};
    installedSkills.forEach((skill) => {
      const key = skill.source_name || "Other";
      if (!map[key]) map[key] = [];
      map[key].push(skill);
    });
    return Object.entries(map);
  }, [installedSkills]);

  const toggleGroup = (key) => setCollapsedGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const handleOpenPanel = () => {
    setCollapsedGroups(new Set(skillGroups.map(([name]) => name)));
    setExpandedSkillMd(null);
    setPanelOpen(true);
  };

  const applyToggle = (skillId) => {
    let parsed = {};
    try { parsed = JSON.parse(toolsJsonContent); } catch {}
    const next = currentSkills.includes(skillId)
      ? currentSkills.filter((id) => id !== skillId)
      : [...currentSkills, skillId];
    onChange(JSON.stringify({ ...parsed, skills: next }, null, 2), next);
  };

  const applyGroupToggle = (groupSkills) => {
    let parsed = {};
    try { parsed = JSON.parse(toolsJsonContent); } catch {}
    const ids = groupSkills.map((s) => s.skill_id);
    const allOn = ids.every((id) => currentSkills.includes(id));
    const next = allOn
      ? currentSkills.filter((id) => !ids.includes(id))
      : [...new Set([...currentSkills, ...ids])];
    onChange(JSON.stringify({ ...parsed, skills: next }, null, 2), next);
  };

  const handleRemoveChip = (skillId) => {
    let parsed = {};
    try { parsed = JSON.parse(toolsJsonContent); } catch {}
    const next = currentSkills.filter((id) => id !== skillId);
    onChange(JSON.stringify({ ...parsed, skills: next }, null, 2), next);
  };

  const getSkillName = (skillId) => {
    const skill = installedSkills.find((s) => s.skill_id === skillId);
    return skill?.display_name || skillId;
  };

  return (
    <div className="border-t border-edge-gray-200 dark:border-edge-gray-700 pt-6">
      <h3 className="text-base font-semibold text-ink-gray-900 dark:text-ink-white mb-1">Skills</h3>
      <p className="text-sm text-ink-gray-500 dark:text-ink-gray-400 mb-4">
        Attach skills to extend this agent's capabilities with reusable instruction sets.
      </p>

      {/* Skills button */}
      <button
        type="button"
        onClick={() => panelOpen ? setPanelOpen(false) : handleOpenPanel()}
        disabled={isLoading || installedSkills.length === 0}
        title={installedSkills.length === 0 ? "No skills installed — add skills from the Forge page" : undefined}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-ink-primary border border-edge-primary rounded-lg hover:bg-fill-primary/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading ? <InlineSpinner /> : panelOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        Skills
      </button>

      {/* Selected skill chips */}
      {currentSkills.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {currentSkills.map((skillId) => (
            <span
              key={skillId}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-fill-primary/10 text-ink-primary dark:bg-fill-primary/20 dark:text-ink-blue-300"
            >
              {getSkillName(skillId)}
              <button
                type="button"
                onClick={() => handleRemoveChip(skillId)}
                className="hover:text-ink-primary-hover dark:hover:text-ink-blue-400 transition-colors"
                aria-label={`Remove ${getSkillName(skillId)}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Inline skill picker panel */}
      {panelOpen && (
        <div className="mt-4 border border-edge-gray-200 dark:border-edge-gray-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-fill-gray-50 dark:bg-fill-deep-surface border-b border-edge-gray-200 dark:border-edge-gray-700">
            <span className="text-xs font-semibold text-ink-gray-600 dark:text-ink-gray-300">
              {currentSkills.length} skill{currentSkills.length !== 1 ? "s" : ""} selected
            </span>
            <button
              type="button"
              onClick={() => setPanelOpen(false)}
              className="text-xs text-ink-gray-400 hover:text-ink-gray-600 dark:hover:text-ink-gray-300 transition-colors"
            >
              Done
            </button>
          </div>

          <div className="divide-y divide-edge-gray-100 dark:divide-edge-gray-800">
            {skillGroups.map(([sourceName, groupSkills]) => {
              const groupIds = groupSkills.map((s) => s.skill_id);
              const allOn = groupIds.every((id) => currentSkills.includes(id));
              const someOn = groupIds.some((id) => currentSkills.includes(id));
              const isCollapsed = collapsedGroups.has(sourceName);

              return (
                <div key={sourceName}>
                  {/* Source group header */}
                  <div className="flex items-center gap-2 px-4 py-3 bg-fill-gray-50 dark:bg-fill-hex-1a1a1c">
                    <input
                      type="checkbox"
                      checked={allOn}
                      ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                      onChange={() => applyGroupToggle(groupSkills)}
                      className="w-3.5 h-3.5 text-ink-primary rounded focus:ring-focus-primary cursor-pointer flex-shrink-0"
                    />
                    <button
                      type="button"
                      onClick={() => toggleGroup(sourceName)}
                      className="flex-1 flex items-center justify-between text-left min-w-0"
                    >
                      <span className="text-xs font-semibold text-ink-ink dark:text-ink-white truncate">{sourceName}</span>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-[10px] text-ink-gray-400">{groupSkills.length} skill{groupSkills.length !== 1 ? "s" : ""}</span>
                        {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-ink-gray-400" /> : <ChevronUp className="w-3.5 h-3.5 text-ink-gray-400" />}
                      </div>
                    </button>
                  </div>

                  {/* Skill rows */}
                  {!isCollapsed && (
                    <div className="divide-y divide-edge-gray-100 dark:divide-edge-gray-800">
                      {groupSkills.map((skill) => (
                        <SkillRow
                          key={skill.skill_id}
                          skill={skill}
                          checked={currentSkills.includes(skill.skill_id)}
                          onToggle={() => applyToggle(skill.skill_id)}
                          mdOpen={expandedSkillMd === skill.skill_id}
                          onToggleMd={() => setExpandedSkillMd((prev) => prev === skill.skill_id ? null : skill.skill_id)}
                          orgId={orgId}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SkillRow = ({ skill, checked, onToggle, mdOpen, onToggleMd, orgId }) => {
  const { data: skillMdData, isLoading: mdLoading } = useGetSkillMd(orgId, skill.skill_id, { enabled: mdOpen });
  const skillMd = skillMdData?.skill_md || "";

  return (
    <div className={`bg-fill-white dark:bg-fill-hex-232326 ${checked ? "bg-fill-primary/5 dark:bg-fill-primary/10" : ""}`}>
      <div className="flex items-start gap-3 px-4 py-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="mt-0.5 w-3.5 h-3.5 text-ink-primary rounded focus:ring-focus-primary cursor-pointer flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-ink-ink dark:text-ink-white">{skill.display_name}</span>
            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${
              skill.scope === "agent_only"
                ? "bg-fill-hex-f3e8ff text-ink-hex-7e22ce dark:bg-fill-hex-581c87/30 dark:text-ink-hex-c084fc"
                : "bg-fill-blue-100 text-ink-primary dark:bg-fill-primary/20 dark:text-ink-blue-300"
            }`}>
              {skill.scope === "agent_only" ? "Agent Only" : "All"}
            </span>
            {checked && <Check className="w-3 h-3 text-ink-primary" />}
          </div>
          {skill.description && (
            <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">{skill.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onToggleMd}
          className="p-1 rounded hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 transition-colors flex-shrink-0"
          title="View SKILL.md"
        >
          {mdOpen ? <ChevronUp className="w-3.5 h-3.5 text-ink-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-ink-gray-400" />}
        </button>
      </div>

      {/* Inline SKILL.md expansion */}
      {mdOpen && (
        <div className="px-4 pb-3 border-t border-edge-gray-100 dark:border-edge-gray-800">
          {mdLoading ? (
            <div className="flex items-center gap-2 py-3 text-xs text-ink-gray-400"><InlineSpinner size="sm" /> Loading SKILL.md…</div>
          ) : skillMd ? (
            <pre className="mt-2 text-[11px] text-ink-gray-700 dark:text-ink-gray-300 bg-fill-gray-50 dark:bg-fill-hex-1a1a1c rounded-lg p-3 overflow-auto max-h-64 whitespace-pre-wrap font-mono leading-relaxed">
              {skillMd}
            </pre>
          ) : (
            <p className="mt-2 text-xs text-ink-gray-400 italic">No SKILL.md available</p>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentSkillsSection;
